import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

// ============= TYPES =============

export type CarrRelevanceTier = 'Tier 1: Must-Attend' | 'Tier 2: High-Value' | 'Tier 3: Monitor';
export type EventStatus = 'upcoming' | 'attending' | 'attended' | 'cancelled' | 'watching';

export interface EventRow {
  id: string;
  event_name: string;
  event_type: string;
  location: string | null;
  start_date: string;
  end_date: string | null;
  website_url: string | null;
  description: string | null;
  status: EventStatus;
  booth_number: string | null;
  budget_estimate: number | null;
  team_members: string[] | null;
  notes: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  // New enrichment fields
  city: string | null;
  state: string | null;
  country: string | null;
  venue: string | null;
  organizer: string | null;
  focus_areas: string[] | null;
  estimated_attendance: number | null;
  registration_url: string | null;
  registration_deadline: string | null;
  data_source: string | null;
  carr_relevance_score: number | null;
  carr_relevance_tier: CarrRelevanceTier | null;
  carr_event_category: string | null;
  target_buyer_personas: string[] | null;
  competitor_presence_likely: boolean;
  territory: string | null;
  strategic_notes: string | null;
  recommended_action: string | null;
  hub_event_id: string | null;
  metadata: Record<string, any> | null;
  // Computed
  account_count: number;
  contact_count: number;
}

export interface EventStats {
  total_events: number;
  upcoming_events: number;
  tier_1_count: number;
  tier_2_count: number;
  tier_3_count: number;
  next_30_days: number;
  attending_count: number;
  linked_accounts: number;
}

export interface EventAccountRow {
  id: string;
  event_id: string;
  company_id: string;
  match_type: string;
  notes: string | null;
  created_at: string;
  company_name?: string;
  modalities?: string[] | null;
  clinical_phase?: string | null;
  territory?: string | null;
}

export interface CreateEventInput {
  event_name: string;
  event_type: string;
  start_date: string;
  end_date?: string | null;
  location?: string | null;
  website_url?: string | null;
  description?: string | null;
  status?: string;
  booth_number?: string | null;
  budget_estimate?: number | null;
  team_members?: string[] | null;
  notes?: string | null;
  created_by?: string | null;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  venue?: string | null;
  organizer?: string | null;
  focus_areas?: string[] | null;
  carr_relevance_tier?: string | null;
  carr_event_category?: string | null;
  territory?: string | null;
  recommended_action?: string | null;
}

export interface UpdateEventInput extends Partial<CreateEventInput> {
  id: string;
}

// ============= QUERY HOOKS =============

export function useEvents() {
  return useQuery({
    queryKey: ['events'],
    queryFn: async (): Promise<EventRow[]> => {
      const { data: events, error } = await (supabase as any)
        .from('v_events_list')
        .select('*')
        .order('start_date', { ascending: true });

      if (error) {
        // Fallback to events table if view doesn't work
        const { data: fallback, error: fbError } = await (supabase as any)
          .from('events')
          .select('*')
          .order('start_date', { ascending: true });
        if (fbError) throw fbError;
        return (fallback || []).map((e: any) => ({ ...e, account_count: 0, contact_count: 0 }));
      }

      return (events || []) as EventRow[];
    },
    staleTime: 60 * 1000,
  });
}

export function useEvent(id: string | null) {
  return useQuery({
    queryKey: ['event', id],
    queryFn: async (): Promise<EventRow | null> => {
      if (!id) return null;

      const { data, error } = await (supabase as any)
        .from('v_events_list')
        .select('*')
        .eq('id', id)
        .single();

      if (error) {
        const { data: fallback, error: fbError } = await (supabase as any)
          .from('events')
          .select('*')
          .eq('id', id)
          .single();
        if (fbError) throw fbError;
        return { ...fallback, account_count: 0, contact_count: 0 } as EventRow;
      }

      return data as EventRow;
    },
    enabled: !!id,
  });
}

export function useEventStats() {
  return useQuery({
    queryKey: ['events-stats'],
    queryFn: async (): Promise<EventStats> => {
      const { data, error } = await (supabase as any)
        .from('v_events_stats')
        .select('*')
        .single();

      if (error) {
        return {
          total_events: 0, upcoming_events: 0, tier_1_count: 0,
          tier_2_count: 0, tier_3_count: 0, next_30_days: 0,
          attending_count: 0, linked_accounts: 0,
        };
      }

      return data as EventStats;
    },
    staleTime: 60 * 1000,
  });
}

export function useEventAccounts(eventId: string | null) {
  return useQuery({
    queryKey: ['event-accounts', eventId],
    queryFn: async (): Promise<EventAccountRow[]> => {
      if (!eventId) return [];

      const { data, error } = await (supabase as any)
        .from('event_accounts')
        .select('*, companies:company_id(company_name, modalities, clinical_phase, territory)')
        .eq('event_id', eventId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      return (data || []).map((row: any) => ({
        id: row.id,
        event_id: row.event_id,
        company_id: row.company_id,
        match_type: row.match_type,
        notes: row.notes,
        created_at: row.created_at,
        company_name: row.companies?.company_name,
        modalities: row.companies?.modalities,
        clinical_phase: row.companies?.clinical_phase,
        territory: row.companies?.territory,
      }));
    },
    enabled: !!eventId,
  });
}

// ============= MUTATION HOOKS =============

export function useCreateEvent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateEventInput) => {
      const { data, error } = await (supabase as any)
        .from('events')
        .insert({
          event_name: input.event_name,
          event_type: input.event_type,
          start_date: input.start_date,
          end_date: input.end_date || null,
          location: input.location || null,
          website_url: input.website_url || null,
          description: input.description || null,
          status: input.status || 'upcoming',
          booth_number: input.booth_number || null,
          budget_estimate: input.budget_estimate || null,
          team_members: input.team_members || null,
          notes: input.notes || null,
          created_by: input.created_by || null,
          city: input.city || null,
          state: input.state || null,
          country: input.country || null,
          venue: input.venue || null,
          organizer: input.organizer || null,
          focus_areas: input.focus_areas || null,
          carr_relevance_tier: input.carr_relevance_tier || null,
          carr_event_category: input.carr_event_category || null,
          territory: input.territory || null,
          recommended_action: input.recommended_action || null,
        })
        .select('id')
        .single();

      if (error) throw error;
      return data?.id as string;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
      queryClient.invalidateQueries({ queryKey: ['events-stats'] });
      toast.success('Event created successfully');
    },
    onError: (err: any) => {
      toast.error(err.message || 'Failed to create event');
    },
  });
}

export function useUpdateEvent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateEventInput) => {
      const { id, ...updates } = input;
      const { error } = await (supabase as any)
        .from('events')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
      queryClient.invalidateQueries({ queryKey: ['event'] });
      queryClient.invalidateQueries({ queryKey: ['events-stats'] });
      toast.success('Event updated');
    },
    onError: (err: any) => {
      toast.error(err.message || 'Failed to update event');
    },
  });
}

export function useDeleteEvent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (eventId: string) => {
      const { error } = await (supabase as any)
        .from('events')
        .delete()
        .eq('id', eventId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
      queryClient.invalidateQueries({ queryKey: ['events-stats'] });
      toast.success('Event deleted');
    },
    onError: (err: any) => {
      toast.error(err.message || 'Failed to delete event');
    },
  });
}
